import {
  BooleanInput,
  Edit,
  ListButton,
  ShowButton,
  SimpleForm,
  TextInput,
  TopToolbar,
  useEditController,
  useGetIdentity,
  useNotify,
  usePermissions,
  useRedirect,
  useStore,
  useUpdate,
  useDataProvider,
} from 'react-admin'
import {
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import TagsInput from '../components/VendorTagsInput'
import userPermissions from '../Users/userPermissions'
import VendorFormLayout from '../components/VendorFormLayout'

const VendorEditActions = () => (
  <TopToolbar>
    <ShowButton />
    <ListButton />
  </TopToolbar>
)

// Define the form section type
interface FormField {
  label: string
  source: string
  required: boolean
}

interface FormSection {
  title: string
  fields: FormField[]
}

interface RawInputProps {
  source: string
  disabled?: boolean
  required?: boolean
}

const RawInput = ({
  source,
  disabled = false,
  required = false,
}: RawInputProps) => {
  const { register, control } = useFormContext()
  const value = useWatch({ name: source, control })
  return (
    <input
      {...register(source, { required })}
      value={value ?? ''}
      disabled={disabled}
      style={{
        width: '100%',
        height: '32px',
        border: '1px solid #ccc',
        padding: '2px 6px',
        fontSize: '14px',
        backgroundColor: disabled ? '#f5f5f5' : 'white',
        boxSizing: 'border-box',
        outline: 'none',
        transition: 'border-color 0.2s',
      }}
      onFocus={(e) => {
        e.currentTarget.style.border = '1px solid #4da3ff'
      }}
      onBlur={(e) => {
        e.currentTarget.style.border = '1px solid #ccc'
      }}
    />
  )
}

const FieldRow = ({
  label,
  source,
  required,
}: {
  label: string
  source: string
  required?: boolean
}) => {
  const { control } = useFormContext()
  const value = useWatch({ name: source, control })
  const isMissingRequired = required && (value === '' || value == null)

  return (
    <TableRow key={source}>
      <TableCell
        sx={{
          bgcolor: '#d9d9d9',
          width: '100%',
          p: 0.25,
          textAlign: 'left',
          verticalAlign: 'middle',
        }}
      >
        {label}
        {isMissingRequired && (
          <span style={{ color: 'red', marginLeft: 4 }}>*</span>
        )}
      </TableCell>
      <TableCell sx={{ p: 0.25 }}>
        <RawInput source={source} required={required} />
      </TableCell>
    </TableRow>
  )
}

const VendorEdit = () => {
  const dataProvider = useDataProvider()
  // State to store the form sections
  const [formSections, setFormSections] = useState<FormSection[]>([])
  const [loading, setLoading] = useState(true)

  const transform = (data) => {
    const tagIds = data.tags.map((t) => t.id)

    let specialFields: { source: string; value: string }[] = []

    // Use the loaded form sections to create special fields
    specialFields = formSections
      .flatMap((section) => section.fields)
      .map(({ label, source }) => ({
        source,
        value: data[source] ?? '',
      }))
      .filter((tag) => tag.value !== '' && tag.source !== 'name')

    return {
      ...data,
      territory_id: 1,
      tags: [...tagIds, ...specialFields],
    }
  }

  const { id } = useParams()
  const notify = useNotify()
  const redirect = useRedirect()
  const { isLoading: identityLoading } = useGetIdentity()
  const { record, isLoading } = useEditController({
    resource: 'vendors',
    id,
  })
  const [actingAs] = useStore('organisation.actingAs')
  const { permissions } = usePermissions()
  const [update] = useUpdate()

  // Load the organisation's vendor form configuration
  useEffect(() => {
    const loadVendorForm = async () => {
      try {
        setLoading(true)
        const organisationId = record?.organisation_id
        const endpoint = organisationId
          ? `vendor-forms/organisations/${organisationId}`
          : 'organisations/current/vendor-forms'

        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        // Check if we got a list of vendor forms or a single vendor form
        if (Array.isArray(response.data)) {
          // We got a list of vendor forms, use the first one if available
          if (response.data.length > 0) {
            setFormSections(response.data[0].vendor_form || [])
          } else {
            // Use empty form sections if no vendor forms exist
            setFormSections([])
          }
        } else if (response.data?.vendor_form) {
          // We got a single vendor form
          setFormSections(response.data.vendor_form)
        } else {
          // Use empty form sections if no configuration is found
          setFormSections([])
        }
      } catch (error) {
        console.error('Error loading vendor form:', error)
        notify('Error loading vendor form configuration', { type: 'error' })
        // Use empty form sections on error
        setFormSections([])
      } finally {
        setLoading(false)
      }
    }

    loadVendorForm()
  }, [dataProvider, notify, record?.organisation_id])

  // Prepare default values for form fields based on tags
  const defaultValues = useMemo(() => {
    if (!record || !formSections.length) return {}

    // Create a map of source -> tag for quick lookup
    const sourceToTagMap = {}

    // Process all tags
    if (record.tags) {
      record.tags.forEach((tag) => {
        if (tag.tag_category?.name) {
          // Use the tag category name as the source
          sourceToTagMap[tag.tag_category.name] = tag
        }
      })
    }

    // For each form field, set its value if there's a matching tag
    const formData = {}
    formSections.forEach((section) => {
      section.fields.forEach((field) => {
        // If the field has source "name", use the vendor's name
        if (field.source === 'name') {
          formData[field.source] = record.name
        } else {
          const matchingTag = sourceToTagMap[field.source]
          if (matchingTag) {
            formData[field.source] = matchingTag.name
          }
        }
      })
    })

    return formData
  }, [record, formSections])

  if (identityLoading || isLoading || loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )

  let fullEdit = false
  if (
    permissions & userPermissions.SUPER_ADMIN &&
    actingAs?.organisationId === record.organisation.id
  ) {
    fullEdit = true
  }

  const vendorSave = (rawdata) => {
    const data = transform(rawdata)

    if (fullEdit) {
      update(
        'vendors',
        { id, data },
        {
          onError: (error) => {
            let errorMessage = 'Failed to update'
            if (error instanceof Error) {
              errorMessage = error.message
            }
            notify(`${errorMessage}`, { type: 'error' })
          },
          onSuccess: () => {
            notify(`Vendor updated`, { type: 'success' })
            redirect('show', 'vendors', id)
          },
        }
      )
    } else {
      update(
        'vendors_tags',
        { id, data },
        {
          onError: (error) => {
            let errorMessage = 'Failed to update'
            if (error instanceof Error) {
              errorMessage = error.message
            }
            notify(`${errorMessage}`, { type: 'error' })
          },
          onSuccess: () => {
            notify(`Vendor updated`, { type: 'success' })
            redirect('show', 'vendors', id)
          },
        }
      )
    }
  }

  return (
    <Edit
      mutationMode="pessimistic"
      actions={<VendorEditActions />}
      transform={transform}
    >
      <SimpleForm
        onSubmit={vendorSave}
        defaultValues={defaultValues}
        sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}
      >
        {fullEdit && <TextInput source="name" />}

        {formSections && formSections.length > 0 ? (
          <VendorFormLayout
            formSections={formSections}
            renderField={(field) => (
              <RawInput source={field.source} required={field.required} />
            )}
          />
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
            <Table size="medium">
              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={2}
                    sx={{
                      bgcolor: '#0077c8',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '16px',
                      p: 1.5,
                    }}
                  >
                    Vendor Basic Information
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      bgcolor: '#d9d9d9',
                      width: '100%',
                      p: 0.25,
                      textAlign: 'left',
                      verticalAlign: 'middle',
                    }}
                  >
                    Vendor Name
                  </TableCell>
                  <TableCell sx={{ p: 0.25 }}>
                    {record?.name || 'N/A'}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <TagsInput
          source="tags"
          // Filter out tags that are already represented in the form fields
          filter={(tag) => {
            // If no form sections, show all tags
            if (!formSections || formSections.length === 0) return true

            // Get all sources from form sections
            const formSources = formSections
              .flatMap((section) => section.fields)
              .map((field) => field.source)

            // If tag has a category and its name matches a form source, filter it out
            return !(
              tag.tag_category && formSources.includes(tag.tag_category.name)
            )
          }}
        />

        <BooleanInput
          source="update_transaction_tags"
          label="Apply tags update to past transactions"
        />
      </SimpleForm>
    </Edit>
  )
}

export default VendorEdit
